import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useStoreState } from 'easy-peasy';
import { useLocalStorage } from 'react-use';
import dayjs from 'dayjs';
import Button from 'Components/nui/Button';
import UserContent from 'Components/nui/UserContent';
import classNames from 'classnames';
import { getStatus } from '~/utils/bannerUtils';
import { IBanner, useDisplayBanners } from '~/hooks/useBanners';

const Banner = () => {
  const solutionId = useStoreState((state: any) => state.auth.solution?.id);
  const userId = useStoreState((state: any) => state.auth.user?.id);
  const { data: displayBanners = [] } = useDisplayBanners();
  // Closed banner id -> the end it had when closed. It stays closed across
  // refreshes, solution switches and logins until it ends, and shows again if
  // its end is changed. Ended entries are dropped here, so storage doesn't grow
  const [stored, setClosed] = useLocalStorage<Record<string, string>>(
    `closed-banners_${userId}`
  );
  // Also kept in memory, so × still works when storage refuses the write
  const [closedNow, setClosedNow] = useState<Record<string, string>>({});
  const closed = Object.fromEntries(
    Object.entries({ ...stored, ...closedNow }).filter(([, end]) =>
      dayjs(end).isAfter(dayjs())
    )
  );

  const banners = useMemo(
    () =>
      displayBanners.filter((banner) => {
        if (getStatus(banner) !== 'live') return false;
        if (!banner.solution) return true;
        const bannerSolutionId =
          typeof banner.solution === 'string'
            ? banner.solution
            : banner.solution.id;
        if (bannerSolutionId === solutionId) return true;
        return false;
      }),
    [displayBanners, solutionId]
  );

  const close = (banner: IBanner) => {
    setClosedNow((state) => ({ ...state, [banner.id]: banner.end }));
    setClosed({ ...closed, [banner.id]: banner.end });
  };

  if (banners.length === 0) return null;

  return (
    <div>
      {banners
        .filter((banner) => closed[banner.id] !== banner.end)
        .map((banner) => (
          <BannerItem
            key={banner.id}
            banner={banner}
            onClose={() => close(banner)}
          />
        ))}
    </div>
  );
};

interface BannerItemProps {
  banner: IBanner;
  onClose: () => void;
}

const BannerItem = ({ banner, onClose }: BannerItemProps) => {
  return (
    <div
      className={classNames('nui-banner', 'nui-alert', 'has-icon', {
        info: banner.style === 'info',
        warning: banner.style === 'warning',
      })}
    >
      {banner.subject && (
        <h4>
          {banner.start && (
            <span className="date">{dayjs(banner.start).format('l')} | </span>
          )}
          {banner.subject}
        </h4>
      )}
      {banner.content && <UserContent data={banner.content} />}

      {banner.action_url && banner.action_text && (
        <>
          {banner.action_url.startsWith('http') ? (
            <a
              href={banner.action_url}
              target="_blank"
              rel="noopener noreferrer"
            >
              {banner.action_text}
            </a>
          ) : (
            <Link to={banner.action_url}>{banner.action_text}</Link>
          )}
        </>
      )}
      <Button
        htmlType="button"
        type="reverse"
        className="closeme"
        onClick={onClose}
      >
        &times;
      </Button>
    </div>
  );
};

export default Banner;
